import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, ArrowLeft } from "lucide-react";
import billoraDark from "../assets/billora-dark.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Password recovery state
  const [recoveryDialogOpen, setRecoveryDialogOpen] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoverySending, setRecoverySending] = useState(false);
  const [recoverySuccess, setRecoverySuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await login(email, password);
      toast.success("¡Bienvenido!");
      // Redirect based on user role
      let targetRoute = "/dashboard";
      if (response?.user?.role === "superadmin") {
        targetRoute = "/superadmin";
      } else if (response?.user?.role === "contador") {
        targetRoute = "/accounting";
      }
      navigate(targetRoute);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    setRecoverySending(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email: recoveryEmail });
      setRecoverySuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar email de recuperación");
    } finally {
      setRecoverySending(false);
    }
  };

  const openRecoveryDialog = () => {
    setRecoveryEmail(email); // Pre-fill with login email if available
    setRecoverySuccess(false);
    setRecoveryDialogOpen(true);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="mb-8">
            <img src={billoraDark} alt="Billora" className="h-24 mb-3" />
            <p className="text-sm text-slate-500">
              Sistema de gestión para estudios jurídicos
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-700">
                Correo electrónico
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="abogado@estudio.cl"
                required
                className="mt-1.5 input-tb"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700">
                  Contraseña
                </Label>
                <button
                  type="button"
                  onClick={openRecoveryDialog}
                  className="text-xs text-[#002FA7] hover:underline"
                  data-testid="forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="input-tb pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-2.5"
              data-testid="login-submit-button"
            >
              {loading ? "Ingresando..." : "Ingresar"}
            </Button>
          </form>
        </div>
      </div>

      {/* Right side - Image (hidden on mobile) */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://customer-assets.emergentagent.com/job_trackbill-2/artifacts/7hymdrm1_law_firmapp.jpg"
          alt="Legal office"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <blockquote className="text-xl font-serif italic">
            "El tiempo es el recurso más valioso de un abogado. Gestiona cada minuto con precisión."
          </blockquote>
        </div>
      </div>

      {/* Password Recovery Dialog */}
      <Dialog open={recoveryDialogOpen} onOpenChange={setRecoveryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {recoverySuccess ? "Revisa tu correo" : "Recuperar contraseña"}
            </DialogTitle>
            <DialogDescription>
              {recoverySuccess 
                ? "Te hemos enviado un enlace para restablecer tu contraseña."
                : "Ingresa tu correo electrónico y te enviaremos instrucciones para recuperar tu contraseña."
              }
            </DialogDescription>
          </DialogHeader>

          {recoverySuccess ? (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-sm text-slate-600">
                Si existe una cuenta con el correo <strong>{recoveryEmail}</strong>, 
                recibirás un email con las instrucciones.
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Revisa también tu carpeta de spam.
              </p>
            </div>
          ) : (
            <form onSubmit={handleRecoverySubmit} className="space-y-4">
              <div>
                <Label htmlFor="recovery-email">Correo electrónico</Label>
                <Input
                  id="recovery-email"
                  type="email"
                  value={recoveryEmail}
                  onChange={(e) => setRecoveryEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  required
                  className="mt-1.5"
                  data-testid="recovery-email-input"
                />
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRecoveryDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={recoverySending}
                  className="btn-primary"
                  data-testid="send-recovery-email"
                >
                  {recoverySending ? "Enviando..." : "Enviar instrucciones"}
                </Button>
              </DialogFooter>
            </form>
          )}

          {recoverySuccess && (
            <DialogFooter>
              <Button
                onClick={() => setRecoveryDialogOpen(false)}
                className="w-full btn-primary"
              >
                Volver al inicio de sesión
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
